/**
 * FERP GraphQL Client Hook - Backend Integration
 *
 * Provides standardized GraphQL client with tenant isolation and audit trails.
 * CRITICAL: All data access must go through repositories - this is the
 * low-level client hook that repositories use.
 */

import { useCallback } from 'react';
import type { ApolloClient, DocumentNode, QueryOptions, MutationOptions } from '@apollo/client';
import { useAuth } from '../security/useAuth';

// GraphQL client interface for repository layer
export interface GraphQLClientInterface {
  query<TData = any, TVariables = any>(options: {
    query: DocumentNode | string;
    variables?: TVariables;
    context?: Record<string, unknown>;
  }): Promise<{ data: TData; errors?: any[] }>;

  mutate<TData = any, TVariables = any>(options: {
    mutation: DocumentNode | string;
    variables?: TVariables;
    context?: Record<string, unknown>;
  }): Promise<{ data: TData; errors?: any[] }>;
}

/**
 * Hook that provides GraphQL client with automatic tenant header injection
 * and audit trail integration. Used by repository layer.
 */
export function useGraphQLClient(): GraphQLClientInterface {
  const { getCurrentTenantId, getAuthHeaders } = useAuth();

  const query = useCallback(async <TData = any, TVariables = any>(options: {
    query: DocumentNode | string;
    variables?: TVariables;
    context?: Record<string, unknown>;
  }): Promise<{ data: TData; errors?: any[] }> => {
    try {
      // Get tenant ID and auth headers for request
      const tenantId = getCurrentTenantId();
      const authHeaders = getAuthHeaders();

      // For development - use fetch with proper headers
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant': tenantId,
          ...authHeaders,
          ...options.context
        },
        body: JSON.stringify({
          query: typeof options.query === 'string' ? options.query : options.query.loc?.source.body,
          variables: options.variables || {}
        })
      });

      if (!response.ok) {
        throw new Error(`GraphQL HTTP error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors && result.errors.length > 0) {
        console.error('[GraphQL Errors]', result.errors);
      }

      return result;

    } catch (error) {
      console.error('[GraphQL Client Error]', error);
      throw error;
    }
  }, [getCurrentTenantId, getAuthHeaders]);

  const mutate = useCallback(async <TData = any, TVariables = any>(options: {
    mutation: DocumentNode | string;
    variables?: TVariables;
    context?: Record<string, unknown>;
  }): Promise<{ data: TData; errors?: any[] }> => {
    try {
      // Get tenant ID and auth headers for request
      const tenantId = getCurrentTenantId();
      const authHeaders = getAuthHeaders();

      // For development - use fetch with proper headers
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant': tenantId,
          ...authHeaders,
          ...options.context
        },
        body: JSON.stringify({
          query: typeof options.mutation === 'string' ? options.mutation : options.mutation.loc?.source.body,
          variables: options.variables || {}
        })
      });

      if (!response.ok) {
        throw new Error(`GraphQL HTTP error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors && result.errors.length > 0) {
        console.error('[GraphQL Errors]', result.errors);
      }

      // Audit trail for mutations
      console.log(`[AUDIT] GraphQL mutation executed for tenant ${tenantId}:`, {
        mutation: typeof options.mutation === 'string' ? 'string query' : 'DocumentNode',
        variables: options.variables,
        timestamp: new Date().toISOString()
      });

      return result;

    } catch (error) {
      console.error('[GraphQL Client Error]', error);
      throw error;
    }
  }, [getCurrentTenantId, getAuthHeaders]);

  return { query, mutate };
}

/**
 * Mock GraphQL client for testing and development
 */
export function useMockGraphQLClient(): GraphQLClientInterface {
  const query = useCallback(async <TData = any>(options: {
    query: DocumentNode | string;
    variables?: any;
  }): Promise<{ data: TData; errors?: any[] }> => {
    // Mock implementation for testing
    console.log('[Mock GraphQL] Query executed:', options);
    return { data: {} as TData };
  }, []);

  const mutate = useCallback(async <TData = any>(options: {
    mutation: DocumentNode | string;
    variables?: any;
  }): Promise<{ data: TData; errors?: any[] }> => {
    // Mock implementation for testing
    console.log('[Mock GraphQL] Mutation executed:', options);
    return { data: {} as TData };
  }, []);

  return { query, mutate };
}